import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography, Stack } from "@mui/material";
import { CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { useReferentialContext } from "../../Context";
import { getNeonColor } from "../../theme";
import { useTheme } from "@mui/system";
import { RSO_REDIRECT_URI } from "./rsoConfig";

type CallbackStatus = "loading" | "success" | "error";

/**
 * This component handles the OAuth2 callback from Riot Games.
 * It is rendered inside a popup window opened from the Profile page.
 *
 * Flow:
 * 1. Riot redirects the popup here with ?code=...
 * 2. We send the code to our backend, which exchanges it for tokens and links the account
 * 3. We post a message back to the parent (Profile page) window with the result
 * 4. The popup closes automatically
 */
export default function RiotCallback() {
    const [status, setStatus] = useState<CallbackStatus>("loading");
    const [message, setMessage] = useState("Linking your Riot account...");
    const context = useReferentialContext();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const error = params.get("error");

        if (error) {
            setStatus("error");
            setMessage(`Authorization denied: ${error}`);
            notifyParent({ success: false, error: `Authorization denied: ${error}` });
            return;
        }

        if (!code) {
            setStatus("error");
            setMessage("No authorization code received.");
            notifyParent({ success: false, error: "No authorization code received." });
            return;
        }

        // Send the authorization code to the backend.
        // The backend will exchange it for tokens via Riot's token endpoint
        // and persist the linked account.
        linkAccount(code);
    }, []);

    async function linkAccount(code: string) {
        try {
            const response = await fetch(`${context.reckonApi.url}/LinkRiotAccount`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Token ${context.user.token}`,
                },
                body: JSON.stringify({
                    code,
                    redirect_uri: RSO_REDIRECT_URI,
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `HTTP ${response.status}`);
            }

            const data = await response.json();
            setStatus("success");
            setMessage(
                data.riot_gamename
                    ? `Linked as ${data.riot_gamename}#${data.riot_tagline}`
                    : "Riot account linked successfully!"
            );
            notifyParent({
                success: true,
                account: {
                    gamename: data.riot_gamename,
                    tagline: data.riot_tagline,
                    puuid: data.riot_puuid,
                },
            });
        } catch (e: any) {
            setStatus("error");
            setMessage(e.message || "Failed to link Riot account.");
            notifyParent({ success: false, error: e.message });
        }
    }

    function notifyParent(payload: object) {
        if (window.opener) {
            window.opener.postMessage(
                { type: "riot-auth-callback", ...payload },
                window.location.origin
            );
            // Auto-close popup after a short delay so the user can see the result
            setTimeout(() => window.close(), 2000);
        }
    }

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                minHeight: "300px",
            }}
        >
            <Stack spacing={3} sx={{ alignItems: "center", textAlign: "center", p: 4 }}>
                {status === "loading" && (
                    <CircularProgress size={48} sx={{ color: neonColor }} />
                )}
                {status === "success" && (
                    <CheckCircle sx={{ fontSize: 56, color: "#4caf50" }} />
                )}
                {status === "error" && (
                    <ErrorIcon sx={{ fontSize: 56, color: "#f44336" }} />
                )}
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: status === "error" ? "#f44336" : "text.primary",
                    }}
                >
                    {message}
                </Typography>
                {status !== "loading" && !window.opener && (
                    <Typography variant="body2" color="text.secondary">
                        You can close this window.
                    </Typography>
                )}
            </Stack>
        </Box>
    );
}
